import { memo } from 'react';
import PropTypes from 'prop-types';

/** @type {Record<string, string>} */
const VARIANT_ICON_RING = {
  blue: 'border-blue-500/35 bg-blue-500/10 text-blue-400',
  purple: 'border-purple-500/35 bg-purple-500/10 text-purple-400',
  green: 'border-emerald-500/35 bg-emerald-500/10 text-emerald-400',
  amber: 'border-amber-500/35 bg-amber-500/10 text-amber-400',
  red: 'border-red-500/35 bg-red-500/10 text-red-400',
  neutral: 'border-edge bg-surface-muted text-content-secondary',
};

/**
 * Vertical audit / governance timeline with connecting line and variant-colored nodes.
 *
 * @param {Array<{
 *   title: string,
 *   description?: import('react').ReactNode,
 *   timestamp?: string,
 *   icon?: import('lucide-react').LucideIcon,
 *   variant?: 'blue'|'purple'|'green'|'amber'|'red'|'neutral',
 *   badge?: import('react').ReactNode,
 *   meta?: import('react').ReactNode,
 *   actor?: import('react').ReactNode,
 *   status?: import('react').ReactNode,
 * }>} props.items
 * @param {string} [props.className]
 */
const DrawerTimeline = ({ items = [], className = '' }) => {
  const list = Array.isArray(items) ? items : [];

  return (
    <div className={['relative pl-1', className].filter(Boolean).join(' ')}>
      <div
        className="absolute bottom-2 left-[15px] top-2 w-px bg-content-faint/25"
        aria-hidden
      />
      <ul className="space-y-6">
        {list.map((item, idx) => {
          const variant = item.variant && VARIANT_ICON_RING[item.variant] ? item.variant : 'neutral';
          const ring = VARIANT_ICON_RING[variant];
          const Icon = item.icon ?? null;

          return (
            <li key={item.key ?? `tl-${idx}`} className="relative flex gap-3">
              <div
                className={[
                  'relative z-10 mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full border',
                  ring,
                ].join(' ')}
              >
                {Icon ? <Icon className="size-3.5" aria-hidden /> : (
                  <span className="size-2 rounded-full bg-current opacity-60" aria-hidden />
                )}
              </div>

              <div className="min-w-0 flex-1 space-y-1.5 pb-1">
                <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1">
                  <h4 className="text-sm font-semibold text-content">{item.title}</h4>
                  {item.timestamp ? (
                    <time className="shrink-0 text-xs text-content-muted">{item.timestamp}</time>
                  ) : null}
                </div>

                {item.actor ? (
                  <p className="text-xs text-content-secondary">{item.actor}</p>
                ) : null}

                {item.description ? (
                  <p className="text-sm leading-relaxed text-content-muted">{item.description}</p>
                ) : null}

                {item.status ? (
                  <div className="text-xs font-medium text-content-secondary">{item.status}</div>
                ) : null}

                {(item.badge != null || item.meta != null) ? (
                  <div className="flex flex-wrap items-center gap-2 pt-0.5">
                    {item.badge != null ? (
                      <span className="inline-flex items-center rounded-full border border-edge bg-surface-muted px-2 py-0.5 text-[11px] text-content-muted">
                        {item.badge}
                      </span>
                    ) : null}
                    {item.meta != null ? (
                      <span className="inline-flex items-center gap-1 text-[11px] text-content-faint">
                        {item.meta}
                      </span>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

DrawerTimeline.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      title: PropTypes.node.isRequired,
      description: PropTypes.node,
      timestamp: PropTypes.string,
      icon: PropTypes.elementType,
      variant: PropTypes.oneOf(['blue', 'purple', 'green', 'amber', 'red', 'neutral']),
      badge: PropTypes.node,
      meta: PropTypes.node,
      actor: PropTypes.node,
      status: PropTypes.node,
    }),
  ),
  className: PropTypes.string,
};

export default memo(DrawerTimeline);
